// src/features/chat/hooks/useAssistantRun.js

import { useCallback, useEffect, useRef, useState } from 'react';
import { streamAssistantChat, normalizeAssistantImages } from '../services/assistantClient';
import { isGroupTarget } from '../models/chatTarget';

/**
 * @typedef {'thinking' | 'streaming' | 'final' | 'error'} PendingPhase
 *
 * @typedef {Object} PendingState
 * @property {string} inReplyTo - The user message ID this is replying to
 * @property {PendingPhase} phase - Current streaming phase
 * @property {string} text - Accumulated response text
 * @property {Array} recordCitations - Backend-resolved Record citations
 * @property {Array} pendingDrafts - Draft IDs created during streaming
 * @property {Object|null} runtime - Optional runtime metadata
 */

/**
 * Hook for managing assistant streaming responses with RAF-buffered updates.
 *
 * Key features:
 * - Buffers streaming chunks and flushes on requestAnimationFrame (~60fps max)
 * - Clean abort semantics for cancellation
 * - Tracks pending drafts created during streaming
 *
 * @returns {Object} Streaming state and controls
 */
export function useAssistantRun() {
  const currentRunRef = useRef(null);
  const nextRunIdRef = useRef(1);

  /** @type {[PendingState | null, Function]} */
  const [pending, setPending] = useState(null);
  const [latestRuntime, setLatestRuntime] = useState(null);

  // Buffer for streaming chunks - accumulate between RAF frames
  const bufferRef = useRef('');
  const rafRef = useRef(0);

  // Flush buffered text to state
  const flush = useCallback(() => {
    rafRef.current = 0;
    const delta = bufferRef.current;
    if (!delta) return;

    bufferRef.current = '';
    setPending((p) =>
      p ? { ...p, phase: 'streaming', text: (p.text || '') + delta } : p
    );
  }, []);

  // Schedule a flush on next animation frame
  const scheduleFlush = useCallback(() => {
    if (rafRef.current) return; // Already scheduled
    rafRef.current = requestAnimationFrame(flush);
  }, [flush]);

  const isCurrentRun = useCallback((run) => {
    return currentRunRef.current?.id === run.id;
  }, []);

  const isVisibleRun = useCallback((run) => {
    return isCurrentRun(run) && currentRunRef.current?.visible;
  }, [isCurrentRun]);

  const abortRun = useCallback((run) => {
    run?.controller?.abort();
  }, []);

  // Cancel the current run, including detached non-visible tail work.
  const cancel = useCallback(() => {
    const currentRun = currentRunRef.current;
    if (currentRun) {
      abortRun(currentRun);
      currentRunRef.current = null;
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
  }, [abortRun]);

  /**
   * Release a run from visible pending UI ownership without aborting it.
   * Used once the persisted assistant reply is observed; any remaining callable
   * work is post-response materialization and should not block the next send.
   * @param {string} [messageId]
   */
  const release = useCallback((messageId) => {
    const currentRun = currentRunRef.current;
    if (!currentRun) return;
    if (messageId && currentRun.messageId !== messageId) return;
    currentRunRef.current = {
      ...currentRun,
      visible: false,
    };
  }, []);

  const isCancellationError = useCallback((err) => {
    return err?.name === 'AbortError' ||
      err?.code === 'functions/cancelled' ||
      err?.code === 'cancelled' ||
      err?.message === 'Request was cancelled.';
  }, []);

  /**
   * Start streaming an assistant response.
   * @param {Object} params
   * @param {string} [params.messageId] - User message ID
   * @param {string} [params.clientSendId] - Private send idempotency key
   * @param {import('../models/chatTarget').ChatTarget} params.target - Chat target
   * @param {string} params.text - User message text
   * @param {Array} params.images - User message images
   * @param {string} params.messageCreatedAtIso - ISO timestamp of user message
   * @param {Object} [params.context] - Optional context (locale, timezone)
   * @param {string} [params.primitiveSelectionId] - Optional primitive selection token
   * @param {Function} [params.onMessageCommitted] - Private server commit callback
   * @returns {Promise<Object>} Final response
   */
  const start = useCallback(
    async ({
      messageId,
      clientSendId,
      target,
      text,
      images = [],
      messageCreatedAtIso,
      context,
      primitiveSelectionId,
      onMessageCommitted,
    }) => {
      const previousRun = currentRunRef.current;
      if (previousRun?.visible) {
        abortRun(previousRun);
      }

      const runId = nextRunIdRef.current;
      nextRunIdRef.current += 1;
      const controller = new AbortController();
      const run = {
        id: runId,
        controller,
        messageId: messageId || clientSendId,
        visible: true,
      };
      currentRunRef.current = run;

      // Initialize pending state
      setLatestRuntime(null);
      setPending({
        inReplyTo: messageId || clientSendId,
        phase: 'thinking',
        text: '',
        recordCitations: [],
        pendingDrafts: [],
        runtime: null,
      });
      bufferRef.current = '';

      // Build payload
      const payload = {
        message: text,
        images: normalizeAssistantImages(images),
        chatKind: target.kind,
        context,
        ...(isGroupTarget(target)
          ? {
              messageId,
              messageCreatedAt: messageCreatedAtIso,
              groupId: target.groupId,
            }
          : {
              clientSendId,
              ...(primitiveSelectionId ? { primitiveSelectionId } : {}),
            }),
      };

      try {
        const final = await streamAssistantChat(payload, {
          signal: controller.signal,
          onChunk: (chunk) => {
            if (!chunk) return;
            if (!isVisibleRun(run)) return;

            if (chunk.type === 'message_committed' && chunk.messageId) {
              onMessageCommitted?.({
                clientSendId: chunk.clientSendId,
                messageId: chunk.messageId,
                messageCreatedAt: chunk.messageCreatedAt,
              });
              currentRunRef.current = {
                ...currentRunRef.current,
                messageId: chunk.messageId,
              };
              setPending((p) =>
                p
                  ? {
                      ...p,
                      inReplyTo: chunk.messageId,
                    }
                  : p
              );
            }

            // Handle text deltas - buffer instead of immediate state update
            if (chunk.type === 'text_delta' && chunk.text) {
              bufferRef.current += chunk.text;
              scheduleFlush();
            }

            // Handle draft creation
            if (chunk.type === 'draft_created' && chunk.draftId) {
              setPending((p) =>
                p
                  ? {
                      ...p,
                      pendingDrafts: [...p.pendingDrafts, { draftId: chunk.draftId }],
                    }
                  : p
              );
            }

          },
        });

        // Ensure buffer is flushed before finalizing
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = 0;
        }
        if (isVisibleRun(run)) {
          flush();
        }

        if (isCurrentRun(run)) {
          setLatestRuntime(final?.runtime || null);
          if (final?.messageId) {
            currentRunRef.current = {
              ...currentRunRef.current,
              messageId: final.messageId,
            };
          }
        }
        // Finalize with server response data
        if (isVisibleRun(run)) {
          setPending((p) =>
            p
              ? {
                  ...p,
                  inReplyTo: final?.messageId || p.inReplyTo,
                  phase: 'final',
                  text: final?.text || p.text,
                  recordCitations:
                    final?.recordCitations || p.recordCitations,
                  runtime: final?.runtime ?? p.runtime ?? null,
                }
              : p
          );
        }

        return final;
      } catch (err) {
        // Ignore only cancellations caused by this local run controller.
        if (controller.signal.aborted && isCancellationError(err)) {
          return null;
        }

        // Mark as error state
        if (isVisibleRun(run)) {
          setPending((p) => (p ? { ...p, phase: 'error' } : p));
        }
        throw err;
      } finally {
        if (isCurrentRun(run)) {
          currentRunRef.current = null;
        }
      }
    },
    [
      abortRun,
      flush,
      isCancellationError,
      isCurrentRun,
      isVisibleRun,
      scheduleFlush,
    ]
  );

  // Clear pending state
  const clear = useCallback(() => {
    setPending(null);
    setLatestRuntime(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);

  return {
    /** Current pending assistant state */
    pending,
    /** Optional runtime metadata from the latest assistant response */
    latestRuntime,
    /** Start a new streaming run */
    start,
    /** Cancel the current stream */
    cancel,
    /** Release visible pending ownership while allowing post-response work to finish */
    release,
    /** Clear pending state */
    clear,
  };
}
